/**
 * The live `/insights` container (#583/#584 N1 slice S12; design D6, D7, D9, L24).
 *
 * ONE SNAPSHOT, EVERY REPRESENTATION. The container asks the server to build ONE stored family report
 * snapshot (`POST /api/insights/snapshots`, S10) and draws the report from that snapshot only. The
 * chart and the table are its stored rows (`lib/insightsMapping.js`), the drill-in pages through ITS stored
 * facts (`GET …/snapshots/:id/facts`, S11) and the CSV link names IT (`…/snapshots/:id/export`, S11). There is no
 * other read of report numbers, and no fallback to one: a snapshot that comes back malformed, or that its
 * drill-in finds expired, withdrawn or missing, takes every number off the screen and says why. A report
 * that passes its own expiry while on screen stays, but its context line says it has expired and its
 * download turns off.
 *
 * A CHANGED FILTER IS A NEW SNAPSHOT. Until it arrives, the old report stays on screen under its OLD
 * heading. If it fails, the old report stays and the failure is said above it, unless the server refused it
 * for lost authority (401 or 403): then every number goes, the old report included. Only a request's own
 * success replaces the chart, the table, the context and the CSV link together (D6). A request that is no
 * longer the newest is dropped, so an answer that arrives late never overwrites a newer one.
 *
 * WHO. The server decides: only a current parent may build or read a snapshot, and a child, a device or
 * a demoted parent is refused there (D9). This screen shows that refusal in words and no numbers.
 *
 * FAMILY ACTIVITY is a SECOND, live read, drawn outside the report and labelled as one. It is the D-60
 * list (default off, unranked, name order) with the owner's Q4 measure, approved XP,
 * read from `GET /api/family-activity` for the report's OWN range (it was `GET /api/achievements/leaderboard`,
 * retired with 410 by #583 audit finding 12, d-20260924-13),
 * and read again whenever the report changes. Off shows no member.
 *
 * WHEN. The first report waits until the sign-in check has answered (d-20260930-08). On a reload the
 * routes mount while `GET /api/auth/verify` is still out. Its answer used to re-key the providers above
 * every route and remount this screen, and building before then sent a second report for the same
 * visit. Since d-20261001-04 that first answer keeps the screen, and the wait still builds for the
 * actor the server named. Until the answer comes, the screen shows its loading state. A check that FAILS is an answer
 * too: nothing remounts on a fault, so the report is built and the server decides who may see it.
 * A check that never answers must not strand the screen: after `SESSION_WAIT_MS` it builds anyway,
 * and the server decides as before. An answer that arrives after that keeps the screen too, so it
 * builds nothing more: one report.
 *
 * WHO CAN BE PICKED. A snapshot names only the members it was built for, so the picker cannot be drawn from
 * the report on screen: after a one-member report it offered that one member and nobody else (D-G, #583 live
 * run). The members of the last all-member report are kept and offered instead. They go with the report when
 * authority is lost. A request the server refuses because it no longer knows a member it named (400
 * `UNKNOWN_PROFILE`) takes every member of that request out of the picker, since the refusal does not say which.
 *
 * MEMORY. Everything is component state: it lives only while this screen is mounted, for the identity
 * that built it, and is gone on sign-out or a switch of family, which unmount it. Answers that arrive
 * after unmounting are dropped.
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import { api, apiUrl } from '../api/client.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { DEFAULT_FILTERS, factLine, reportMessage, toActivity, toReport, toSnapshotBody } from '../lib/insightsMapping.js';

const Insights = lazy(() => import('../pages/Insights.jsx'));

export const MALFORMED =
  'The report came back in a shape this screen cannot read, so no numbers are shown. Try again.';
const EXPIRED_EXPORT = 'This report has expired, so it cannot be downloaded. Show the report again to build a new one.';
const OFFLINE_EXPORT = 'Downloading needs a connection. The numbers above are the ones this report saved.';
const REPORT_GONE = new Set([404, 410]);
// ponytail: a fixed guess at how long the sign-in check may take, not a measured latency. The upgrade is a
// request timeout on the verify call in `api/client.js`, so the check itself settles as `unavailable`.
export const SESSION_WAIT_MS = 10_000;

const factsPath = (id, metric, cursor) =>
  `/api/insights/snapshots/${id}/facts?metric=${metric}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;

export default function InsightsRoute() {
  // Outside an ActorProvider (a component test) there is no check to wait for.
  const actorStatus = useOptionalActor()?.status;
  const sessionAnswered = actorStatus !== 'loading';
  const [view, setView] = useState({ status: 'loading', report: null, filters: DEFAULT_FILTERS, error: null });
  const [pending, setPending] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [facts, setFacts] = useState({});
  const [activity, setActivity] = useState(null);
  const [members, setMembers] = useState(null);
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const [, setTick] = useState(0);
  const [waitedOut, setWaitedOut] = useState(false);
  const mounted = useRef(true);
  const latest = useRef(0);
  const builtOnMount = useRef(false);
  const currentReport = useRef(null);
  const pendingFacts = useRef(new Set());

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  /** Nothing of this report may stay on screen: drop it and say why. */
  const withdraw = useCallback((message) => {
    latest.current += 1;
    currentReport.current = null;
    setView((v) => ({ ...v, status: 'error', report: null, error: message }));
    setFacts({});
    setActivity(null);
    setMembers(null);
    setPending(false);
    setRefreshError(null);
  }, []);

  const build = useCallback((filters) => {
    const id = ++latest.current;
    setPending(true);
    setRefreshError(null);
    api.post('/api/insights/snapshots', toSnapshotBody(filters))
      .then((body) => {
        const report = toReport(body?.snapshot);
        if (!report) throw Object.assign(new Error('malformed snapshot'), { malformed: true });
        return report;
      })
      .then((report) => {
        if (!mounted.current || id !== latest.current) return;
        currentReport.current = report.id;
        // A member-filtered report adds anyone it names that the list lacks, and never shrinks the list.
        setMembers((known) => (report.filters.profileIds === 'all' ? report.profiles
          : [...(known ?? []), ...report.profiles.filter((p) => !known?.some((k) => k.id === p.id))]));
        setView({ status: 'ready', report, filters, error: null });
        setFacts({});
        setPending(false);
      })
      .catch((error) => {
        if (!mounted.current || id !== latest.current) return;
        const message = error?.malformed ? MALFORMED : reportMessage(error);
        if (error?.status === 401 || error?.status === 403) {
          withdraw(message);
          return;
        }
        // The server no longer knows a member this request named (L6): they left the family mid-session. It does
        // not say which one, so none of them stays pickable. No other refusal takes anyone out of the picker.
        if (error?.status === 400 && error?.body?.code === 'UNKNOWN_PROFILE' && Array.isArray(filters.profileIds)) {
          setMembers((known) => known && known.filter((m) => !filters.profileIds.includes(m.id)));
        }
        setPending(false);
        setView((v) => (v.report ? v : { ...v, status: 'error', error: message }));
        setRefreshError(message);
      });
  }, [withdraw]);

  // A check that never answers: stop waiting after the bound (WHEN, above).
  useEffect(() => {
    if (sessionAnswered) return undefined;
    const timer = setTimeout(() => setWaitedOut(true), SESSION_WAIT_MS);
    return () => clearTimeout(timer);
  }, [sessionAnswered]);

  useEffect(() => {
    // Not yet: the sign-in check has not answered (WHEN, above).
    // This counts on no re-verify being reachable while the actor is unknown; a future periodic
    // re-verify would need this gate revisited.
    if (!sessionAnswered && !waitedOut) return;
    // StrictMode replays effects within one visit. A real remount gets a fresh ref.
    if (builtOnMount.current) return;
    builtOnMount.current = true;
    build(DEFAULT_FILTERS);
  }, [build, sessionAnswered, waitedOut]);

  // The family activity list, for the range of the report on screen.
  const report = view.report;
  const rangeKey = report ? `${report.id}|${report.range.startDate}|${report.range.endDate}` : null;
  useEffect(() => {
    if (!report) return undefined;
    const controller = new AbortController();
    let live = true;
    setActivity({ status: 'loading', rows: [] });
    const { startDate, endDate } = report.range;
    api.get(`/api/family-activity?metric=approved_xp&startDate=${startDate}&endDate=${endDate}`, { signal: controller.signal })
      .then((body) => { if (live) setActivity(toActivity(body)); })
      .catch(() => { if (live && !controller.signal.aborted) setActivity({ status: 'error', rows: [] }); });
    return () => { live = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rangeKey names exactly the report fields read here
  }, [rangeKey]);

  // Offline turns the download off; the report itself stays, labelled as possibly out of date.
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);

  // Re-render at the moment the report expires, so its download turns off without a click.
  useEffect(() => {
    if (!report) return undefined;
    const wait = Date.parse(report.expiresAt) - Date.now();
    if (!(wait > 0)) return undefined;
    const timer = setTimeout(() => setTick((n) => n + 1), Math.min(wait + 50, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [report]);

  const loadFacts = useCallback((metric, cursor) => {
    const snapshot = report;
    if (!snapshot) return;
    const requestKey = `${snapshot.id}:${metric}:${cursor ?? ''}`;
    if (pendingFacts.current.has(requestKey)) return;
    pendingFacts.current.add(requestKey);
    setFacts((f) => ({ ...f, [metric]: { lines: [], ...f[metric], open: true, status: 'loading' } }));
    api.get(factsPath(snapshot.id, metric, cursor))
      .then((page) => {
        if (!mounted.current || currentReport.current !== snapshot.id) return;
        const lines = (Array.isArray(page?.facts) ? page.facts : [])
          .map((fact, i) => ({ id: `${fact?.type}:${fact?.id ?? i}`, text: factLine(fact ?? {}, snapshot) }));
        setFacts((f) => ({ ...f, [metric]: { open: true, status: 'ready', lines: [...(cursor ? f[metric]?.lines ?? [] : []), ...lines],
          nextCursor: typeof page?.nextCursor === 'string' ? page.nextCursor : null } }));
      })
      .catch((error) => {
        if (!mounted.current || currentReport.current !== snapshot.id) return;
        if (REPORT_GONE.has(error?.status) || error?.status === 403 || error?.status === 401) {
          withdraw(reportMessage(error));
          return;
        }
        setFacts((f) => ({ ...f, [metric]: { ...f[metric], open: true, status: 'error' } }));
      })
      .finally(() => { pendingFacts.current.delete(requestKey); });
  }, [report, withdraw]);

  const toggleFacts = useCallback((metric) => {
    if (facts[metric]?.open) setFacts((f) => ({ ...f, [metric]: { ...f[metric], open: false } }));
    else if (facts[metric]?.status === 'ready') setFacts((f) => ({ ...f, [metric]: { ...f[metric], open: true } }));
    else loadFacts(metric);
  }, [facts, loadFacts]);

  const moreFacts = useCallback((metric) => loadFacts(metric, facts[metric]?.nextCursor), [facts, loadFacts]);

  const expired = report ? Date.parse(report.expiresAt) <= Date.now() : false;
  const exportHref = report && online && !expired ? apiUrl(`/api/insights/snapshots/${report.id}/export`) : null;

  return (
    <Suspense fallback={<LanternLoader label="Loading your family report…" />}>
      <Insights
        showSnapshotNotice={false}
        report={report}
        filters={view.filters}
        members={members}
        onApplyFilters={build}
        pending={pending && !!report}
        refreshError={report ? refreshError : null}
        facts={facts}
        onToggleFacts={toggleFacts}
        onMoreFacts={moreFacts}
        exportHref={exportHref}
        exportNote={expired ? EXPIRED_EXPORT : OFFLINE_EXPORT}
        expired={expired}
        offline={!online}
        activity={activity}
        loading={view.status === 'loading'}
        error={view.status === 'error' ? view.error : null}
        onRetry={() => build(view.filters)}
      />
    </Suspense>
  );
}
